import * as THREE from 'three'
import { Component } from '@ecs/Component'


export class TransformData {
    position: THREE.Vector3
    rotation: THREE.Euler
    scale: THREE.Vector3
    get quaternionRotation(): THREE.Quaternion { return new THREE.Quaternion().setFromEuler(this.rotation) }
    set quaternionRotation(rotation: THREE.Quaternion) { this.rotation = new THREE.Euler().setFromQuaternion(rotation) }
    get matrix(): THREE.Matrix4 { return this.ToMatrix4() }
    get forward(): THREE.Vector3 { return this.VectorLocalToWorld(new THREE.Vector3(0, 0, 1)) }
    constructor(position: THREE.Vector3 = new THREE.Vector3(0, 0, 0), rotation: THREE.Euler | THREE.Quaternion = new THREE.Euler(0, 0, 0), scale: THREE.Vector3 = new THREE.Vector3(1, 1, 1)) {
        this.rotation = new THREE.Euler()
        this.position = new THREE.Vector3().copy(position)
        if (rotation instanceof THREE.Quaternion) this.quaternionRotation = rotation
        else this.rotation = new THREE.Euler().copy(rotation)
        this.scale = new THREE.Vector3().copy(scale)
    }
    PositionLocalToWorld(vec: THREE.Vector3): THREE.Vector3 {
        return vec.applyMatrix4(this.ToMatrix4(true, true, false));
    }
    VectorLocalToWorld(vec: THREE.Vector3): THREE.Vector3 {
        return vec.applyMatrix4(this.ToMatrix4(false, true, false));
    }
    ToMatrix4(translation = true, rotation = true, scale = true): THREE.Matrix4 {
        const m = new THREE.Matrix4();
        const pos = translation ? this.position : new THREE.Vector3(0, 0, 0);
        const quat = rotation ? this.quaternionRotation : new THREE.Quaternion();
        const scl = scale ? this.scale : new THREE.Vector3(1, 1, 1);
        m.compose(pos, quat, scl);
        return m
    }
    FromMatrix4(m: THREE.Matrix4): TransformData {
        const quat = new THREE.Quaternion()
        m.decompose(this.position, quat, this.scale)
        this.quaternionRotation = quat
        return this
    }
    // for this, the 3D space referance swap from world to other
    TransformWorldToLocal(other: TransformData): TransformData {
        return new TransformData().FromMatrix4(other.matrix.invert().multiply(this.matrix))
    }
    // for this, the 3D space referance swap from other to world
    TransformLocalToWorld(other: TransformData): TransformData {
        return new TransformData().FromMatrix4(other.matrix.multiply(this.matrix))
    }
    lookAt(target: THREE.Vector3, order: THREE.EulerOrder = 'YXZ'): TransformData {
        const m = new THREE.Matrix4();
        m.lookAt(this.position, target, new THREE.Vector3(0, 1, 0));
        this.rotation.setFromRotationMatrix(m, order);
        return this
    }
    lookAlong(direction: THREE.Vector3, order: THREE.EulerOrder = 'YXZ'): TransformData {
        return this.lookAt(this.position.clone().add(direction), order);
    }
    clone(): TransformData { return new TransformData(this.position.clone(), this.rotation.clone(), this.scale.clone()) }
    copy(other: TransformData): TransformData { this.position.copy(other.position); this.rotation.copy(other.rotation); this.scale.copy(other.scale); return this }
    equals(other: TransformData): boolean { return this.position.equals(other.position) && this.rotation.equals(other.rotation) && this.scale.equals(other.scale) }
    Lerp(other: TransformData, t: number): TransformData {
        return this.LerpB(other, t, t, t)
    }
    LerpB(other: TransformData, posFac: number, rotFac: number, scaleFac: number): TransformData {
        this.position.lerp(other.position, posFac)
        const quaternion = this.quaternionRotation
        const otherQuaternion = other.quaternionRotation.clone()
        quaternion.slerp(otherQuaternion, rotFac)
        this.quaternionRotation = quaternion
        this.scale.lerp(other.scale, scaleFac)
        return this
    }
    setScale(scale: THREE.Vector3): TransformData {
        this.scale.copy(scale)
        return this
    }
    setPosition(position: THREE.Vector3): TransformData {
        this.position.copy(position)
        return this
    }
    setRotation(rotation: THREE.Euler): TransformData {
        this.rotation.copy(rotation)
        return this
    }
}

export class Transform extends Component {
    lastTransformData!: TransformData
    transformData!: TransformData
    get position(): THREE.Vector3 { return this.transformData.position }
    get rotation(): THREE.Euler { return this.transformData.rotation }
    get scale(): THREE.Vector3 { return this.transformData.scale }

    get localTransform(): TransformData { return this.gameObject.parent ? this.transformData.TransformWorldToLocal(this.gameObject.parent.transform.transformData) : this.transformData }

    set position(position: THREE.Vector3) { this.transformData.position = position; this.markDirty() }
    set rotation(rotation: THREE.Euler) { this.transformData.rotation = rotation; this.markDirty() }
    set scale(scale: THREE.Vector3) { this.transformData.scale = scale; this.markDirty() }

    get forward(): THREE.Vector3 { return this.transformData.forward }

    private _dirty = true

    attachChildren = false
    attachToParent = false

    setup(data: any) {
        this.transformData = new TransformData(data.position, data.rotation, data.scale);
        this.lastTransformData = this.transformData.clone()
    }

    markDirty() {
        this._dirty = true
        //set children transform

        for (const child of this.gameObject.children) {
            if (child.transform) {
                if (this.attachChildren || child.transform.attachToParent) {
                    const deltaMat = this.transformData.matrix.multiply(this.lastTransformData.matrix.invert())
                    child.transform.transformData = new TransformData().FromMatrix4(deltaMat.multiply(child.transform.transformData.matrix))
                    child.transform.markDirty()
                }
            }
        }
        this.lastTransformData = this.transformData.clone()
    }

    isDirty(): boolean {
        return this._dirty
    }

    clearDirty() {
        this._dirty = false
    }

    lookAt(target: THREE.Vector3): void { this.transformData.lookAt(target); this.markDirty() }
    lookAlong(direction: THREE.Vector3): void { this.transformData.lookAlong(direction); this.markDirty() }
    VectorLocalToWorld(vec: THREE.Vector3): THREE.Vector3 { return this.transformData.VectorLocalToWorld(vec) }
    PositionLocalToWorld(vec: THREE.Vector3): THREE.Vector3 { return this.transformData.PositionLocalToWorld(vec) }


    setPosition(x: number, y: number, z: number): void { this.position.set(x, y, z); this.markDirty() }
    setPositionFromVector3(position: THREE.Vector3): void { this.position.copy(position); this.markDirty() }
    setRotation(x: number, y: number, z: number): void { this.rotation.set(x, y, z); this.markDirty() }
    setRotationFromEuler(rotation: THREE.Euler): void { this.rotation.copy(rotation); this.rotation.order = rotation.order; this.markDirty() }
    setScale(x: number, y: number, z: number): void { this.scale.set(x, y, z); this.markDirty() }
    setScaleFromVector3(scale: THREE.Vector3): void { this.scale.copy(scale); this.markDirty() }
}
